/**
 * IconButton — a square, icon-only action. Rests transparent and lifts to a
 * soft rose wash on hover. Requires an accessible `label` (rendered as
 * aria-label). Use `active` for toggle-style controls (e.g. a selected tool).
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";

export type IconButtonSize = "sm" | "md" | "lg";

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label"> {
  /** Accessible label — required since the button has no visible text. */
  readonly label: string;
  /** Control size. @default "md" */
  readonly size?: IconButtonSize;
  /** Pressed / selected state for toggle controls. */
  readonly active?: boolean;
  /** The lucide icon element. */
  readonly children: ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    { label, size = "md", active, className, children, type = "button", ...rest },
    ref,
  ) {
    const classes = ["lc-icon-btn", `lc-icon-btn--${size}`, className]
      .filter(Boolean)
      .join(" ");
    return (
      <button
        ref={ref}
        type={type}
        className={classes}
        aria-label={label}
        title={label}
        aria-pressed={active ?? undefined}
        data-active={active || undefined}
        {...rest}
      >
        {children}
      </button>
    );
  },
);
